import { useCallback, useEffect, useState } from "react";
import { del, get, patch, post } from "../lib/api";
import { useAuth } from "../lib/auth";
import { dataHora } from "../lib/formato";
import { Confirmacao, Modal } from "../components/Modal";
import type { Role, Usuario } from "../lib/tipos";

const ROTULO_PAPEL: Record<Role, string> = {
  ADMIN: "Administrador",
  GESTOR: "Gestor",
  CONSULTOR: "Consultor",
  LEITOR: "Leitor",
};

const DESCRICAO_PAPEL: Record<Role, string> = {
  ADMIN: "Acesso total, incluindo usuários e exclusão de visitas.",
  GESTOR: "Vê todas as visitas e edita as tabelas de valores.",
  CONSULTOR: "Cria e edita apenas as próprias visitas.",
  LEITOR: "Somente leitura — dashboard e consulta.",
};

const COR_PAPEL: Record<Role, { fundo: string; texto: string }> = {
  ADMIN: { fundo: "#f0f0ff", texto: "#6941c6" },
  GESTOR: { fundo: "#e8f5ee", texto: "#0e8c3a" },
  CONSULTOR: { fundo: "#eff8ff", texto: "#1570ef" },
  LEITOR: { fundo: "#f2f4f7", texto: "#475467" },
};

interface Rascunho {
  id?: string;
  nome: string;
  email: string;
  senha: string;
  role: Role;
}

export default function Usuarios() {
  const { usuario: eu } = useAuth();
  const [itens, setItens] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [editando, setEditando] = useState<Rascunho | null>(null);
  const [erroForm, setErroForm] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [desativando, setDesativando] = useState<Usuario | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    return get<Usuario[]>("/api/usuarios")
      .then((u) => { setItens(u); setErro(""); })
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvar() {
    if (!editando) return;
    setErroForm("");
    const novo = !editando.id;

    if (editando.nome.trim().length < 2) return setErroForm("Informe o nome.");
    if (novo && !/^\S+@\S+\.\S+$/.test(editando.email.trim())) return setErroForm("E-mail inválido.");
    if (novo && editando.senha.length < 8) return setErroForm("A senha precisa de ao menos 8 caracteres.");
    if (!novo && editando.senha && editando.senha.length < 8) {
      return setErroForm("A nova senha precisa de ao menos 8 caracteres.");
    }

    setSalvando(true);
    try {
      if (novo) {
        await post("/api/usuarios", {
          nome: editando.nome.trim(),
          email: editando.email.trim().toLowerCase(),
          senha: editando.senha,
          role: editando.role,
        });
      } else {
        await patch(`/api/usuarios/${editando.id}`, {
          nome: editando.nome.trim(),
          role: editando.role,
          ...(editando.senha ? { senha: editando.senha } : {}),
        });
      }
      setEditando(null);
      await carregar();
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSalvando(false);
    }
  }

  async function alternarAtivo(u: Usuario) {
    try {
      if (u.ativo === false) await patch(`/api/usuarios/${u.id}`, { ativo: true });
      else await del(`/api/usuarios/${u.id}`);
      setDesativando(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao alterar o acesso");
      setDesativando(null);
    }
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h2 style={{ fontSize: 18, margin: 0 }}>Usuários</h2>
        <button
          className="botao"
          onClick={() => { setErroForm(""); setEditando({ nome: "", email: "", senha: "", role: "CONSULTOR" }); }}
        >
          Novo usuário
        </button>
      </div>
      <p style={{ fontSize: 13, color: "var(--texto-suave)", margin: "0 0 16px" }}>
        Senhas ficam guardadas apenas como hash Argon2id — nem o administrador consegue lê-las.
        Para dar acesso de volta a quem esqueceu a senha, defina uma nova aqui e passe ao usuário.
      </p>

      {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

      <div className="cartao" style={{ padding: 0, overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th style={{ width: 150 }}>Papel</th>
              <th style={{ width: 160 }}>Último acesso</th>
              <th style={{ width: 110 }}>Situação</th>
              <th style={{ width: 180 }}></th>
            </tr>
          </thead>
          <tbody>
            {itens.map((u) => {
              const cor = COR_PAPEL[u.role];
              const souEu = u.id === eu?.id;
              const inativo = u.ativo === false;
              return (
                <tr key={u.id} style={inativo ? { opacity: 0.55 } : undefined}>
                  <td>
                    <strong>{u.nome}</strong>
                    {souEu && <span style={{ fontSize: 11, color: "var(--texto-suave)", marginLeft: 6 }}>(você)</span>}
                  </td>
                  <td style={{ color: "var(--texto-suave)" }}>{u.email}</td>
                  <td>
                    <span className="etiqueta" style={{ background: cor.fundo, color: cor.texto }}>
                      {ROTULO_PAPEL[u.role]}
                    </span>
                  </td>
                  <td style={{ color: "var(--texto-suave)", fontSize: 13 }}>
                    {dataHora(u.ultimoLogin ?? null)}
                  </td>
                  <td style={{ fontSize: 13, color: inativo ? "#b42318" : "var(--verde)" }}>
                    {inativo ? "Inativo" : "Ativo"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      className="botao secundario"
                      style={{ padding: "5px 10px", fontSize: 12 }}
                      onClick={() => {
                        setErroForm("");
                        setEditando({ id: u.id, nome: u.nome, email: u.email, senha: "", role: u.role });
                      }}
                    >
                      Editar
                    </button>
                    {!souEu && (
                      <button
                        className="botao secundario"
                        style={{ padding: "5px 10px", fontSize: 12, marginLeft: 6, color: inativo ? "var(--verde)" : "#b42318" }}
                        onClick={() => (inativo ? alternarAtivo(u) : setDesativando(u))}
                      >
                        {inativo ? "Reativar" : "Desativar"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!carregando && itens.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--texto-suave)", padding: 32 }}>
                Nenhum usuário cadastrado.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editando && (
        <Modal
          titulo={editando.id ? "Editar usuário" : "Novo usuário"}
          largura={520}
          onFechar={() => setEditando(null)}
          rodape={
            <>
              <button className="botao secundario" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</button>
              <button className="botao" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</button>
            </>
          }
        >
          {erroForm && <div className="erro" style={{ marginBottom: 12 }}>{erroForm}</div>}

          <label className="rotulo">Nome</label>
          <input className="campo" value={editando.nome} onChange={(e) => setEditando({ ...editando, nome: e.target.value })} />

          <label className="rotulo" style={{ marginTop: 12 }}>E-mail</label>
          <input
            className="campo"
            type="email"
            autoComplete="off"
            value={editando.email}
            readOnly={Boolean(editando.id)}
            style={editando.id ? { background: "#f9fafb" } : undefined}
            onChange={(e) => setEditando({ ...editando, email: e.target.value })}
          />
          {editando.id && (
            <div style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 4 }}>
              O e-mail identifica a conta e não pode ser alterado. Crie um novo usuário se precisar trocar.
            </div>
          )}

          <label className="rotulo" style={{ marginTop: 12 }}>
            {editando.id ? "Nova senha (deixe em branco para manter)" : "Senha"}
          </label>
          <input
            className="campo"
            type="password"
            autoComplete="new-password"
            placeholder={editando.id ? "Manter a senha atual" : "Mínimo de 8 caracteres"}
            value={editando.senha}
            onChange={(e) => setEditando({ ...editando, senha: e.target.value })}
          />
          {editando.id && editando.senha && (
            <div style={{ fontSize: 11, color: "#b54708", marginTop: 4 }}>
              Trocar a senha encerra as sessões abertas desse usuário.
            </div>
          )}

          <label className="rotulo" style={{ marginTop: 12 }}>Papel</label>
          <select
            className="campo"
            value={editando.role}
            onChange={(e) => setEditando({ ...editando, role: e.target.value as Role })}
          >
            {(Object.keys(ROTULO_PAPEL) as Role[]).map((r) => (
              <option key={r} value={r}>{ROTULO_PAPEL[r]}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 4 }}>
            {DESCRICAO_PAPEL[editando.role]}
          </div>
          {editando.id === eu?.id && (
            <div style={{ fontSize: 11, color: "#b54708", marginTop: 8 }}>
              Você não pode rebaixar nem desativar a própria conta — a API bloqueia isso para
              evitar que o sistema fique sem nenhum administrador.
            </div>
          )}
        </Modal>
      )}

      {desativando && (
        <Confirmacao
          titulo="Desativar usuário"
          perigo
          rotuloConfirmar="Desativar"
          mensagem={
            <>
              Desativar o acesso de <strong>{desativando.nome}</strong>?
              <div style={{ marginTop: 8, color: "var(--texto-suave)", fontSize: 13 }}>
                As sessões abertas são encerradas na hora. A conta não é excluída — as visitas e
                os eventos de auditoria continuam atribuídos a ela, e você pode reativar depois.
              </div>
            </>
          }
          onConfirmar={() => alternarAtivo(desativando)}
          onCancelar={() => setDesativando(null)}
        />
      )}
    </>
  );
}
